import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Input } from './ui/input';
import { cn } from '../lib/utils';
import { Search, Plus, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Simple in-memory cache for search results
const searchCache = new Map();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * ItemSearchCombobox - Typeahead input for searching Parts List items
 * 
 * Features:
 * - Debounced search (300ms)
 * - Results cached in-memory
 * - "Add new" option for one-off entries
 * - Auto-populates unit_cost from selected item
 * - Mobile-optimized: prevents flicker and scroll jumps
 */
export const ItemSearchCombobox = ({
  value,
  itemId,
  onChange,
  onSelect,
  placeholder = "Search parts or type new item...",
  className,
  disabled = false,
  'data-testid': dataTestId = 'item-search-combobox'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);
  // Track if user is actively typing to prevent re-focus issues
  const isTypingRef = useRef(false);
  // Store last scroll position to restore after dropdown changes
  const lastScrollPosRef = useRef(0);

  // Get auth token
  const getToken = () => {
    // Try access_token first (main auth)
    const accessToken = localStorage.getItem('access_token');
    if (accessToken) {
      return accessToken;
    }
    // Fallback to impersonation token if active
    const impersonationToken = localStorage.getItem('impersonation_token');
    if (impersonationToken) {
      return impersonationToken;
    }
    return null;
  };

  // Search function with caching
  const searchItems = useCallback(async (query) => {
    if (!query || query.length < 1) {
      setResults([]);
      return;
    }

    const cacheKey = query.toLowerCase();
    const cached = searchCache.get(cacheKey);
    
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      setResults(cached.data);
      return;
    }

    setLoading(true);
    try {
      const token = getToken();
      if (!token) {
        setResults([]);
        return;
      }

      const response = await fetch(
        `${API_URL}/api/items/search?q=${encodeURIComponent(query)}&limit=10`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        searchCache.set(cacheKey, { data, timestamp: Date.now() });
        setResults(data);
      } else {
        setResults([]);
      }
    } catch (error) {
      console.error('Item search error:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounced search - simplified to always search when value changes while open
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Search if we have value and dropdown is open
    if (value && value.length >= 1 && isOpen) {
      debounceRef.current = setTimeout(() => {
        searchItems(value);
      }, 300);
    } else if (!value || value.length < 1) {
      setResults([]);
    }

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [value, isOpen, searchItems]);

  // Reset highlight when results change
  useEffect(() => {
    setHighlightIndex(-1);
  }, [results]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle item selection
  const handleSelect = (item) => {
    onSelect({
      item_id: item.id,
      item_name: item.name,
      unit_cost: item.unit_cost
    });
    setIsOpen(false);
    setResults([]);
  };

  // Handle "Add new" (one-off entry)
  const handleAddNew = () => {
    if (value && value.trim()) {
      onSelect({
        item_id: null,
        item_name: value.trim(),
        unit_cost: null
      });
      setIsOpen(false);
      setResults([]);
    }
  };

  // Handle input change
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    isTypingRef.current = true;
    
    // Store scroll position before any state changes
    lastScrollPosRef.current = window.scrollY;
    
    onChange(newValue);
    
    // If user edits text after selecting an item, clear the item_id
    if (itemId) {
      onSelect({
        item_id: null,
        item_name: newValue,
        unit_cost: null
      });
    }
    
    if (newValue.length >= 1) {
      setIsOpen(true);
    }
  };

  // Restore scroll position when results change (prevents mobile jump)
  useEffect(() => {
    if (lastScrollPosRef.current > 0 && isTypingRef.current) {
      // Use requestAnimationFrame to ensure DOM has updated
      requestAnimationFrame(() => {
        window.scrollTo(0, lastScrollPosRef.current);
      });
    }
  }, [results]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' && value) {
        setIsOpen(true);
        return;
      }
      return;
    }

    const totalOptions = results.length + (value && value.trim() ? 1 : 0); // +1 for "Add new"

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightIndex((prev) => (prev + 1) % totalOptions);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightIndex((prev) => (prev - 1 + totalOptions) % totalOptions);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightIndex >= 0 && highlightIndex < results.length) {
          handleSelect(results[highlightIndex]);
        } else if (highlightIndex === results.length || (results.length === 0 && value?.trim())) {
          // "Add new" option
          handleAddNew();
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
      default:
        break;
    }
  };

  // Memoize showAddNew to prevent unnecessary recalculations
  const showAddNew = useMemo(() => {
    return value && value.trim() && !results.some(
      r => r.name.toLowerCase() === value.toLowerCase()
    );
  }, [value, results]);

  // Calculate if dropdown should show - memoized
  const shouldShowDropdown = useMemo(() => {
    return isOpen && (results.length > 0 || showAddNew);
  }, [isOpen, results.length, showAddNew]);

  return (
    <div 
      ref={containerRef}
      className="relative" 
      data-testid={dataTestId}
      // Prevent layout shift on mobile by reserving space
      style={{ minHeight: shouldShowDropdown ? 'auto' : undefined }}
    >
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
        <Input
          ref={inputRef}
          value={value}
          onChange={handleInputChange}
          onFocus={() => {
            // Only open if there's value and we're not in a re-focus loop
            if (value && !isTypingRef.current) {
              setIsOpen(true);
            }
          }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={cn("pl-8", className)}
          disabled={disabled}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
          // Prevent mobile zoom on focus
          style={{ fontSize: '16px' }}
          data-testid={`${dataTestId}-input`}
        />
        {loading && (
          <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground animate-spin" />
        )}
      </div>

      {/* Dropdown - uses position:absolute to prevent layout shift */}
      {shouldShowDropdown && (
        <div
          ref={dropdownRef}
          className="absolute z-50 mt-1 bg-white border border-border rounded-md shadow-lg max-h-60 overflow-y-auto min-w-[320px] md:min-w-[400px] w-max max-w-[calc(100vw-2rem)]"
          // Prevent touch events from bubbling and causing scroll issues
          onTouchStart={(e) => e.stopPropagation()}
          data-testid={`${dataTestId}-dropdown`}
        >
          {/* Search results */}
          {results.map((item, index) => (
            <button
              key={item.id}
              type="button"
              onClick={() => handleSelect(item)}
              className={cn(
                "w-full px-3 py-2 text-left hover:bg-muted flex flex-col gap-0.5 border-b border-border last:border-b-0",
                highlightIndex === index && "bg-muted"
              )}
              data-testid={`${dataTestId}-option-${index}`}
            >
              <span className="font-medium text-sm">{item.name}</span>
              <span className="text-xs text-muted-foreground flex gap-2 flex-wrap">
                {item.sku && <span>SKU: {item.sku}</span>}
                {item.unit_cost && <span>${item.unit_cost.toFixed(2)}</span>}
                {item.category && <span className="truncate">{item.category}</span>}
              </span>
            </button>
          ))}

          {/* "Add new" option */}
          {showAddNew && (
            <button
              type="button"
              onClick={handleAddNew}
              className={cn(
                "w-full px-3 py-2 text-left hover:bg-muted flex items-center gap-2 text-primary",
                highlightIndex === results.length && "bg-muted"
              )}
              data-testid={`${dataTestId}-add-new`}
            >
              <Plus className="w-4 h-4" />
              <span className="text-sm">Add &ldquo;{value.trim()}&rdquo;</span>
            </button>
          )}
        </div>
      )}

      {/* Linked item indicator */}
      {itemId && (
        <span className="absolute -top-1 -right-1 w-2 h-2 bg-green-500 rounded-full" title="Linked to Parts List" />
      )}
    </div>
  );
};

export default ItemSearchCombobox;
